// Compute Summary — Step 5 Results.
// Displays per-site, per-environment compute sizing outcomes.

import type { ComputeResult } from '../../types';

interface Props {
  compute: ComputeResult[];
}

export function ComputeSummary({ compute }: Props) {
  if (compute.length === 0) {
    return <p style={{ color: '#64748b', fontStyle: 'italic' }}>No compute results available.</p>;
  }

  const primary = compute.filter(r => r.site === 'primary');
  const dr = compute.filter(r => r.site === 'dr');

  return (
    <div>
      {primary.length > 0 && (
        <section style={{ marginBottom: 32 }}>
          <SiteHeader label="Primary Site" colour="#1e40af" />
          <div style={gridStyle}>
            {primary.map((r, i) => <ComputeCard key={i} result={r} />)}
          </div>
        </section>
      )}
      {dr.length > 0 && (
        <section>
          <SiteHeader label="DR Site" colour="#9d174d" />
          <div style={gridStyle}>
            {dr.map((r, i) => <ComputeCard key={i} result={r} />)}
          </div>
        </section>
      )}
    </div>
  );
}

function SiteHeader({ label, colour }: { label: string; colour: string }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginBottom: 12,
    }}>
      <div style={{ width: 4, height: 20, borderRadius: 2, background: colour }} />
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#1e293b' }}>{label}</h3>
    </div>
  );
}

function ComputeCard({ result }: { result: ComputeResult }) {
  const envLabel = result.environment.replace('_', ' ');
  const envColour =
    result.environment === 'production' ? '#15803d' :
    result.environment === 'pre_production' ? '#b45309' : '#6366f1';

  const spec = result.recommendedHostSpec;

  return (
    <div style={cardStyle}>
      {/* Environment badge */}
      <div style={{ marginBottom: 12 }}>
        <span style={{
          padding: '3px 10px',
          borderRadius: 9999,
          fontSize: 11,
          fontWeight: 700,
          background: envColour + '1a',
          color: envColour,
          textTransform: 'capitalize',
          letterSpacing: '0.04em',
        }}>
          {envLabel}
        </span>
      </div>

      {/* Ultra-low latency warning */}
      {result.hasUltraLowLatencyVms && (
        <div style={{
          padding: '6px 10px',
          borderRadius: 6,
          background: '#fef3c7',
          borderLeft: '3px solid #f59e0b',
          marginBottom: 10,
          fontSize: 12,
          color: '#92400e',
        }}>
          ⚠ {result.latencyConstraintCount} VM type(s) require ultra-low latency — review co-location policy
        </div>
      )}

      {/* Key metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
        <Metric label="Raw vCPU" value={result.rawVcpuRequired.toLocaleString()} />
        <Metric label="Physical Cores" value={result.physicalCoresRequired.toLocaleString()} />
        <Metric label="Raw Memory" value={`${result.rawMemoryGbRequired.toLocaleString()} GB`} />
        <Metric label="Physical Memory" value={`${result.physicalMemoryGbRequired.toLocaleString()} GB`} />
        <Metric label="Hosts (no HA)" value={result.hostCountWithoutHA.toLocaleString()} />
        <Metric
          label="Hosts (with HA)"
          value={result.hostCountWithHA.toLocaleString()}
          highlight
        />
      </div>

      {/* Host spec */}
      <div style={{
        padding: '8px 10px',
        background: '#f1f5f9',
        borderRadius: 6,
        fontSize: 12,
        color: '#475569',
        marginBottom: 10,
      }}>
        <strong style={{ color: '#1e293b' }}>Recommended host spec:</strong>{' '}
        ≥{spec.physicalCores} cores · ≥{spec.memoryGb} GB RAM · ≥{spec.minCpuGhz} GHz
      </div>

      {/* Notes */}
      {result.notes.length > 0 && (
        <details>
          <summary style={{ fontSize: 12, color: '#64748b', cursor: 'pointer', userSelect: 'none' }}>
            Methodology notes ({result.notes.length})
          </summary>
          <ol style={{ margin: '6px 0 0 0', paddingLeft: 18 }}>
            {result.notes.map((n, i) => (
              <li key={i} style={{ fontSize: 12, color: '#475569', marginBottom: 3, lineHeight: 1.5 }}>{n}</li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}

function Metric({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div style={{
      padding: '6px 8px',
      background: highlight ? '#eff6ff' : '#fafafa',
      borderRadius: 6,
      border: highlight ? '1px solid #bfdbfe' : '1px solid #f1f5f9',
    }}>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{label}</div>
      <div style={{
        fontSize: 16,
        fontWeight: 700,
        color: highlight ? '#1e40af' : '#1e293b',
        fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </div>
    </div>
  );
}

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: 16,
};

const cardStyle: React.CSSProperties = {
  padding: 16,
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  background: '#fff',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};
